// marketingApi.js
import axios from 'axios';
// Base URL of your FastAPI backend
const API_BASE_URL = 'https://plansync-backend-234288438538.us-central1.run.app/api';

/**
 * Upload an image to the server and retrieve its URL.
 * @param {File} file - The image file to upload.
 * @returns {Promise<string>} - The URL of the uploaded image.
 */
export const uploadImage = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(`${API_BASE_URL}/upload-image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // Assuming the server returns { url: 'http://.../image.png' }
    return response.data.url;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw new Error('Failed to upload image. Please try again.');
  }
};

/**
 * Fetch user data based on user ID.
 * @param {string} userId - The user's ID.
 * @returns {Promise<Object>} - The user data.
 */
export const getUserData = async (userId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/user-settings/${userId}`);
    // console.log('getUserData response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching user data:', error);
    throw new Error('Failed to fetch user data.');
  }
};

/**
 * Create a marketing PowerPoint presentation.
 * @param {string} templateName - The template name.
 * @param {string} userId - The user's ID.
 * @param {Object} placeholders - Placeholder data.
 * @param {string} logoUrl - URL to the company logo.
 * @returns {Promise<Blob>} - The generated PowerPoint file.
 */
export const createMarketingPowerPoint = async (templateName, userId, placeholders, logoUrl) => {
  try {
    // console.log('Sending request with:', { templateName, userId, placeholders, logoUrl });

    const response = await axios.post(`${API_BASE_URL}/generate-powerpoint`, {
      template_name: templateName,
      user_id: userId,
      placeholders: placeholders,
      logo_url: logoUrl, // Updated to use logo_url
    }, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'Content-Type': 'application/json',
      },
    });

    // Log response details for debugging


    if (response.status === 200 && response.data instanceof Blob) {
      return response.data;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('Error generating PowerPoint:', error);
    if (error.response) {
      console.error('Error response:', error.response);
      // If the error response is Blob, attempt to parse JSON
      if (error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.detail || 'Unknown error');
        } catch (e) {
          throw new Error(text);
        }
      }
    }
    throw error;
  }
};

/**
 * Create a marketing flyer.
 * @param {string} userid - The user's ID.
 * @param {string} filename - The desired filename for the flyer.
 * @param {string} logoUrl - URL to the company logo.
 * @param {boolean} showLogo - Whether to display the logo.
 * @param {Array<Object>} advisors - List of advisors with their details.
 * @param {Object} companyInfo - Company information.
 * @param {string} colorScheme - Sidebar color in hex.
 * @returns {Promise<Blob>} - The generated flyer PDF.
 */
export const createMarketingFlyer = async (
  userid,
  filename,
  logo_url,
  show_logo,
  advisors,
  company_info,
  sidebar_color
) => {
  try {

    const payload = {
      userid,
      filename,
      logo_url,
      show_logo,
      advisors,
      company_info: {
        ...company_info,
        aum: company_info.aum || '',
      },
      sidebar_color,
    };

    // console.log('Sending flyer request with payload:', payload);

    const response = await axios.post(`${API_BASE_URL}/generate-flyer`, payload, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    // console.log('createMarketingFlyer response:', response.data);

    if (response.status === 200 && response.data instanceof Blob) {
      return response.data;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('Error generating flyer:', error);
    if (error.response) {
      console.error('Error response:', error.response);
      // If the error response is Blob, attempt to parse JSON
      if (error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.detail || 'Unknown error');
        } catch (e) {
          throw new Error(text);
        }
      }
    }
    throw error;
  }
};
